import { faChevronRight } from '@fortawesome/free-solid-svg-icons'
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons/faChevronLeft'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as React from 'react'
import { IPet } from '../../interfaces/types.ts'
import { useSnapshot } from 'valtio'
import { dataState } from '../../store/dataState.ts'
import { drawerState } from '../../store/drawerState.ts'
import { Battery } from '../common/device/Battery.tsx'
import './Dashboard2.css'
import { centerMapOnPet } from '../map/mapHelpers.tsx'
import { DateTime } from 'luxon'
import { authState, effectiveTimezone } from '../../store/authState.ts'
import { controlState } from '../../store/controlState.ts'
import { DeviceControls } from './DeviceControls.tsx'
import { fenceIcon } from '../../helpers/colorHelper.ts'
import { faMoon } from '@fortawesome/free-solid-svg-icons/faMoon'
import { isSleepFeatureEnabled, hasScheduledSleep } from '../../helpers/sleepHelper.ts'
import { AlarmClockIcon } from '../svgs/AlarmClockIcon.tsx'
import { usePetOrder, arrayMove } from '../../hooks/usePetOrder.ts'
import { computeActivityStatus } from '../../helpers/activityStatusHelper.ts'
import { useNowTick } from '../../hooks/useNowTick.ts'
import { DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core'
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

interface Props {
}

/** Wrapper that makes a pet card sortable via dnd-kit */
const SortablePetCard: React.FC<{ id: string; children: React.ReactNode }> = ({ id, children }) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id })
    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition: transition || undefined,
    }
    return (
        <div ref={setNodeRef} style={style} {...attributes} {...listeners} className={'flex items-start show popup'}>
            {children}
        </div>
    )
}

export const Dashboard2: React.FC<Props> = (props) => {
    const { } = props
    const devices = (useSnapshot(dataState) as typeof dataState).devices
    const pets = (useSnapshot(dataState) as typeof dataState).pets
    const account = (useSnapshot(dataState) as typeof dataState).account
    // Subscribe to timezoneOverride so component re-renders when the toggle flips
    useSnapshot(authState)
    const showPetList = useSnapshot(drawerState).petList
    const isMobile = useSnapshot(controlState).isOnMobileDevice

    // Re-render every 30s so activity status (overdue detection) stays current
    // even when no new device updates arrive
    useNowTick()

    // Track expansion state by device id, not index — robust to reorder / list changes.
    const [openIds, setOpenIds] = React.useState<Set<string>>(new Set())

    const sleepFeatureEnabled = isSleepFeatureEnabled(account)

    // Build a petId → device map once per devices change so we don't re-scan on every pet.
    const deviceByPetId = React.useMemo(() => {
        const map = new Map<string, typeof devices[number]>()
        devices.forEach(d => { if (d.petId) map.set(d.petId, d) })
        return map
    }, [devices])

    // Filter + sort pets with devices, memoized so it only runs when inputs change.
    const petsWithDevice = React.useMemo(() => {
        return pets.filter(p => deviceByPetId.has(p._id))
    }, [pets, deviceByPetId])

    // Drag-reorder support with localStorage persistence
    const { orderedPets, saveOrder } = usePetOrder(petsWithDevice)
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
        useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
    )

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event
        if (over && active.id !== over.id) {
            const oldIndex = orderedPets.findIndex(p => p._id === active.id)
            const newIndex = orderedPets.findIndex(p => p._id === over.id)
            if (oldIndex !== -1 && newIndex !== -1) {
                const reordered = arrayMove(orderedPets, oldIndex, newIndex)
                saveOrder(reordered)
            }
        }
    }

    const petsWithDevices = orderedPets.map((pet: IPet) => {
        const d = deviceByPetId.get(pet._id)!
        const isOpen = openIds.has(d._id)

        const tz = effectiveTimezone()
        const locationDt = d?.lastLocation?.properties?.receivedTime
            ? DateTime.fromISO(d.lastLocation.properties.receivedTime).setZone(tz.value)
            : null
        const timezoneOverrideActive = authState.timezoneOverride
        const locationDate = locationDt
            ? locationDt.toLocaleString(DateTime.DATETIME_MED_WITH_WEEKDAY) + (!isMobile && timezoneOverrideActive ? ` ${locationDt.toFormat('ZZZZ')}` : '')
            : 'No locations received'
        const powerSavingMode = d.status.beaconInRange && d.status.powerOn
        const isCharging = d.status.charging
        let findDisabled = !d.status.powerOn || powerSavingMode
        findDisabled = isCharging ? false : findDisabled
        findDisabled = d.status.deviceCommandInProgressStatus ? true : findDisabled
        const safeZoneName = d.status.occupiedZone || 'Safe Zone'

        // const off = !d?.status.powerOn ?
        //     <div
        //         className={'rounded px-1 h-4 m-auto w-fit text-center bg-primary-content text-sm text-accent font-bold leading-[14px]'}>
        //         Gibi Off
        //     </div>
        //     : null

        const displayedDate = (
            <div className={'flex flex-col -mt-1'}>
                <div className={'text-primary-content text-md font-semibold text-nowrap'}>{locationDate}</div>
                {/* {off || inPowerSavingMode || staleWarning}*/}
            </div>
        )

        const topRow = (
            <div className={'flex-1'}>
                <div className={'flex items-center justify-between w-full'}>
                    <div className={'font-bold text-2xl text-left ml-9'}> {pet.petName}</div>
                </div>
            </div>
        )

        const petAvatar = (
            <div className={'rounded-full border border-2 border-white shrink-0'}>
                <div className={'rounded-full border border-4'} style={{ borderColor: pet.iconColor }}>
                    <div className={'rounded-full border border-2 border-white'}>
                        <img className={'rounded-full w-[60px] h-[60px] object-cover'} src={pet.imageURL} alt={pet.petName} />
                    </div>
                </div>
            </div>
        )

        const safeZoneIndicator = (
            <div className={'flex flex-col gap-1 items-center self-start w-[60px] mr-2'}>
                <div
                    className={`font-semibold text-lg flex items-center justify-self-center rounded-full w-[40px] h-[40px] border-2 border-white ${!d.status.safeZoneViolation ? 'bg-success' : 'bg-gray-400'}`}>
                    <img className={'w-9 mt-2 ml-[2px]'}
                        src={fenceIcon(!d.status.safeZoneViolation ? '#ffffff' : '#fff')} alt={'safe zone'} />
                </div>
                <div
                    className={'overflow-hidden bg-transparent text-primary-content justify-self-center'}>
                    {safeZoneName}
                </div>
            </div>
        )

        const beaconIndicator = (
            <div className={'flex flex-col gap-1 items-center relative self-start w-[60px]'}>
                <div
                    className={`flex items-center justify-self-center rounded-full w-[40px] h-[40px] border-2 border-white ${powerSavingMode ? 'bg-success' : 'bg-gray-400'}`}>

                </div>
                <div
                    className={'bg-transparent text-primary-content justify-self-center'}>
                    Power Saving
                </div>
            </div>
        )

        const batteryIndicator = (
            <div className={'self-start w-[60px] flex justify-center'}>
                <Battery pet={pet} iconOnly />
            </div>
        )

        const showSleepIndicator = sleepFeatureEnabled
        const isSleeping = d.status.sleeping === true
        const isScheduled = hasScheduledSleep(d.status)

        // Three states: sleeping (green bg + moon), scheduled (white bg + green clock), idle (gray bg + moon)
        const sleepBg = isSleeping ? 'bg-success' : isScheduled ? 'bg-white' : 'bg-gray-400'

        // Activity status for at-a-glance card styling
        const activityStatus = computeActivityStatus(d)
        const isInactive = activityStatus.level === 'inactive'
        const isDegraded = activityStatus.level === 'degraded'
        const isOverdue = isInactive && activityStatus.reason === 'overdue'

        // Tooltip text for the activity dot
        const activityTooltip = isOverdue ? 'Overdue — device should be messaging'
            : isDegraded ? 'Weak GPS — messaging but no valid locations'
            : activityStatus.reason === 'power_off' ? 'Device is off'
            : activityStatus.reason === 'sleeping' ? 'Device is sleeping'
            : activityStatus.reason === 'charging' ? 'Device is in charger'
            : activityStatus.reason === 'power_saving' ? 'Power saving mode'
            : 'Inactive'

        const sleepIndicator = showSleepIndicator ? (
            <div className={'flex flex-col gap-1 items-center self-start w-[60px]'}>
                <div
                    className={`flex items-center justify-center rounded-full w-[40px] h-[40px] border-2 border-white ${sleepBg}`}>
                    {isScheduled && !isSleeping
                        ? <AlarmClockIcon color="hsl(var(--su))" size={22} />
                        : <FontAwesomeIcon icon={faMoon} className={'text-white text-xl'} />
                    }
                </div>
                <div className={'bg-transparent text-primary-content justify-self-center'}>
                    {isSleeping ? 'Sleeping' : isScheduled ? 'Sleep Scheduled' : 'Sleep'}
                </div>
                {/* {sleepWakeReason && (
                    <div className={'text-xs text-primary-content text-center max-w-[60px] leading-tight'}>
                        {sleepWakeReason}
                    </div>
                )} */}
            </div>
        ) : null

        return (
            <SortablePetCard key={d._id} id={pet._id}>
                <div onClick={() => centerMapOnPet(pet)}>
                    <div
                        className={`relative flex flex-col p-2 w-[27rem] gap-0 bg-primary text-primary-content rounded-xl ml-2 h-[168px]`}>

                        {/* Activity status dot — desktop only */}
                        {!isMobile && (isDegraded || isInactive) && (
                            <div className="tooltip tooltip-left absolute top-2 right-2" data-tip={activityTooltip} role="status" aria-label={activityTooltip}>
                                <div className={`w-3 h-3 rounded-full border border-white/50
                                    ${isOverdue ? 'bg-error animate-pulse' : isDegraded ? 'bg-amber-500 animate-pulse' : 'bg-gray-600'}`}
                                />
                            </div>
                        )}

                        {topRow}

                        <div className={'flex items-center justify-between w-[95%] h-[90px]'}>
                            {petAvatar}
                            <div className={'flex gap-3 items-center justify-evenly pt-6'}>
                                {safeZoneIndicator}
                                {beaconIndicator}
                                {sleepIndicator}
                                {batteryIndicator}
                            </div>
                        </div>
                        <div className={'w-full h-4'} />

                        <button type="button"
                            className={`collapsible p-0 bg-base-300`}
                            onClick={(e) => {
                                e.stopPropagation()
                                setOpenIds(prev => {
                                    const next = new Set(prev)
                                    if (next.has(d._id)) next.delete(d._id)
                                    else next.add(d._id)
                                    return next
                                })
                            }}
                        >
                            <div className="py-0 flex items-center justify-between gap-4">
                                <div className={'text-md text-nowrap ml-2'}>{d.deviceName}</div>
                                {displayedDate}
                                <div className={'w-8'}>
                                    {isOpen ? <FontAwesomeIcon icon={faChevronLeft} className={'ml-2'} /> :
                                        <FontAwesomeIcon icon={faChevronRight} className={'ml-2'} />
                                    }
                                </div>
                            </div>
                        </button>

                    </div>
                </div>
                {isOpen ?
                    <DeviceControls pet={pet} show={true} disabled={Boolean(findDisabled)} />
                    : null}
            </SortablePetCard>
        )
    })

    return (
        <div
            className={`min-w-[260px] flex flex-col items-start gap-2 overflow-x-auto max-h-[75vh] transparentScroll transition-all duration-300 ease-in-out ${showPetList ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4 pointer-events-none'}`}
        >
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={orderedPets.map(p => p._id)} strategy={verticalListSortingStrategy}>
                    {petsWithDevices}
                </SortableContext>
            </DndContext>
        </div>
    )
}
